// QuestionForm.tsx
import React, { useState } from 'react';
import {
    Box,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    SelectChangeEvent,
    IconButton,
    Stack,
    Chip
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { ReviewQuestion } from '../../types';

interface QuestionFormProps {
    initialData?: ReviewQuestion;
    onSubmit: (data: any) => void;
    onCancel: () => void;
}

// Helper function to normalize YES/NO values to uppercase and handle null/undefined
const normalizeYesNoValue = (value: string | null | undefined): string => {
    if (!value) return '';
    const normalized = value.trim().toUpperCase();
    if (normalized === 'YES' || normalized === 'NO') {
        return normalized;
    }
    // Handle variations
    if (normalized === 'Y' || normalized === 'TRUE' || normalized === '1') return 'YES';
    if (normalized === 'N' || normalized === 'FALSE' || normalized === '0') return 'NO';
    return value; // Return original if not recognized
};

const QuestionForm: React.FC<QuestionFormProps> = ({ initialData, onSubmit, onCancel }) => {
    // Normalize YES/NO values if the answer type is YES-NO
    const defaultValue = initialData?.answer_type === 'YES-NO' 
        ? normalizeYesNoValue(initialData?.default_value)
        : initialData?.default_value || '';
        
    const idealValue = initialData?.answer_type === 'YES-NO'
        ? normalizeYesNoValue(initialData?.ideal_value)
        : initialData?.ideal_value || '';
        
    const [formData, setFormData] = useState({
        questionText: initialData?.review_question_text || '',
        description: initialData?.description || '',
        dimension: initialData?.review_dimension || '',
        answerType: initialData?.answer_type || '',
        answerOptions: initialData?.answer_options || [],
        actionRequired: initialData?.action_required || '',
        defaultValue,
        idealValue
    });

    const [newOption, setNewOption] = useState('');

    const handleTextChange = (field: string) => (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value
        }));
    };

    const handleSelectChange = (field: string) => (
        event: SelectChangeEvent
    ) => {
        const newValue = event.target.value;
        
        setFormData(prev => {
            const updates: any = {
                [field]: newValue
            };
            
            // Handle answer type changes
            if (field === 'answerType') {
                // Clear answer options when switching away from SELECT or MULTI-SELECT
                if (newValue !== 'SELECT' && newValue !== 'MULTI-SELECT') {
                    updates.answerOptions = [];
                }
                // Reset default and ideal values when changing answer type
                updates.defaultValue = '';
                updates.idealValue = '';
            }
            
            return {
                ...prev,
                ...updates
            };
        });
    };

    const handleAddOption = () => {
        if (newOption.trim() && !formData.answerOptions.includes(newOption.trim())) {
            setFormData(prev => ({
                ...prev,
                answerOptions: [...prev.answerOptions, newOption.trim()]
            }));
            setNewOption('');
        }
    };

    const handleRemoveOption = (optionToRemove: string) => {
        setFormData(prev => ({
            ...prev,
            answerOptions: prev.answerOptions.filter(option => option !== optionToRemove)
        }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        // Ensure YES/NO values are normalized before submitting
        const submittedData = {...formData};
        if (formData.answerType === 'YES-NO') {
            submittedData.defaultValue = normalizeYesNoValue(formData.defaultValue);
            submittedData.idealValue = normalizeYesNoValue(formData.idealValue);
        }
        onSubmit(submittedData);
    };

    const showOptionsInput = formData.answerType === 'SELECT' || formData.answerType === 'MULTI-SELECT';

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
                fullWidth
                label="Question Text"
                value={formData.questionText}
                onChange={handleTextChange('questionText')}
                required
                margin="normal"
            />
            <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={handleTextChange('description')}
                multiline
                rows={3}
                margin="normal"
            />
            <FormControl fullWidth margin="normal">
                <InputLabel>Dimension</InputLabel>
                <Select
                    value={formData.dimension}
                    onChange={handleSelectChange('dimension')}
                    label="Dimension"
                >
                    <MenuItem value="Schedule">Schedule</MenuItem>
                    <MenuItem value="Effort">Effort</MenuItem>
                    <MenuItem value="Scope">Scope</MenuItem>
                    <MenuItem value="Customer">Customer</MenuItem>
                    <MenuItem value="Skills">Skills</MenuItem>
                    <MenuItem value="Staffing">Staffing</MenuItem>
                    <MenuItem value="Financials">Financials</MenuItem>
                    <MenuItem value="Engineering">Engineering</MenuItem>
                    <MenuItem value="Quality">Quality</MenuItem>
                </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
                <InputLabel>Answer Type</InputLabel>
                <Select
                    value={formData.answerType}
                    onChange={handleSelectChange('answerType')}
                    label="Answer Type"
                >
                    <MenuItem value="YES-NO">Yes/No</MenuItem>
                    <MenuItem value="TEXT">Text</MenuItem>
                    <MenuItem value="NUMBER">Number</MenuItem>
                    <MenuItem value="PERCENTAGE">Percentage</MenuItem>
                    <MenuItem value="SELECT">Select</MenuItem>
                    <MenuItem value="MULTI-SELECT">Multi-Select</MenuItem>
                </Select>
            </FormControl>

            {showOptionsInput && (
                <Box sx={{ mt: 2 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                        <TextField
                            fullWidth
                            label="Add Option"
                            value={newOption}
                            onChange={(e) => setNewOption(e.target.value)}
                            size="small"
                        />
                        <IconButton 
                            onClick={handleAddOption}
                            color="primary"
                            sx={{ ml: 1 }}
                        >
                            <AddIcon />
                        </IconButton>
                    </Stack>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {formData.answerOptions.map((option, index) => (
                            <Chip
                                key={index}
                                label={option}
                                onDelete={() => handleRemoveOption(option)}
                                deleteIcon={<CloseIcon />}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {/* Dynamic Default Value Field based on Answer Type */}
            {formData.answerType === 'YES-NO' ? (
                <FormControl fullWidth margin="normal">
                    <InputLabel>Default Value</InputLabel>
                    <Select
                        value={formData.defaultValue || ''}
                        onChange={handleSelectChange('defaultValue')}
                        label="Default Value"
                    >
                        <MenuItem value="">
                            <em>None</em>
                        </MenuItem>
                        <MenuItem value="YES">YES</MenuItem>
                        <MenuItem value="NO">NO</MenuItem>
                    </Select>
                    <Box sx={{ mt: 0.5, ml: 1.5 }}>
                        <em>Suggested starting value for this question</em>
                    </Box>
                </FormControl>
            ) : formData.answerType === 'SELECT' ? (
                <FormControl fullWidth margin="normal">
                    <InputLabel>Default Value</InputLabel>
                    <Select
                        value={formData.defaultValue || ''}
                        onChange={handleSelectChange('defaultValue')}
                        label="Default Value"
                        disabled={formData.answerOptions.length === 0}
                    >
                        <MenuItem value="">
                            <em>None</em>
                        </MenuItem>
                        {formData.answerOptions.map((option, index) => (
                            <MenuItem key={index} value={option}>{option}</MenuItem>
                        ))}
                    </Select>
                    <Box sx={{ mt: 0.5, ml: 1.5 }}>
                        <em>Suggested starting value for this question</em>
                    </Box>
                </FormControl>
            ) : formData.answerType === 'MULTI-SELECT' ? (
                <FormControl fullWidth margin="normal">
                    <InputLabel>Default Value</InputLabel>
                    <Select
                        value={formData.defaultValue || ''}
                        onChange={handleSelectChange('defaultValue')}
                        label="Default Value"
                        disabled={formData.answerOptions.length === 0}
                    >
                        <MenuItem value="">
                            <em>None</em>
                        </MenuItem>
                        {formData.answerOptions.map((option, index) => (
                            <MenuItem key={index} value={option}>{option}</MenuItem>
                        ))}
                    </Select>
                    <Box sx={{ mt: 0.5, ml: 1.5 }}>
                        <em>Suggested starting value for this question (first selected option)</em>
                    </Box>
                </FormControl>
            ) : (
                <TextField
                    fullWidth
                    label="Default Value"
                    value={formData.defaultValue}
                    onChange={handleTextChange('defaultValue')}
                    margin="normal"
                    helperText="Suggested starting value for this question"
                />
            )}

            {/* Dynamic Ideal Value Field based on Answer Type */}
            {formData.answerType === 'YES-NO' ? (
                <FormControl fullWidth margin="normal">
                    <InputLabel>Ideal Value</InputLabel>
                    <Select
                        value={formData.idealValue || ''}
                        onChange={handleSelectChange('idealValue')}
                        label="Ideal Value"
                    >
                        <MenuItem value="">
                            <em>None</em>
                        </MenuItem>
                        <MenuItem value="YES">YES</MenuItem>
                        <MenuItem value="NO">NO</MenuItem>
                    </Select>
                    <Box sx={{ mt: 0.5, ml: 1.5 }}>
                        <em>Target or optimal value for this question</em>
                    </Box>
                </FormControl>
            ) : formData.answerType === 'SELECT' || formData.answerType === 'MULTI-SELECT' ? (
                <FormControl fullWidth margin="normal">
                    <InputLabel>Ideal Value</InputLabel>
                    <Select
                        multiple
                        value={formData.idealValue ? formData.idealValue.split(',').map(v => v.trim()).filter(Boolean) : []}
                        onChange={(e) => setFormData(prev => ({
                            ...prev,
                            idealValue: (e.target.value as unknown as string[]).join(',')
                        }))}
                        label="Ideal Value"
                        disabled={formData.answerOptions.length === 0}
                        renderValue={(selected) => (selected as string[]).join(', ')}
                    >
                        {formData.answerOptions.map((option, index) => (
                            <MenuItem key={index} value={option}>{option}</MenuItem>
                        ))}
                    </Select>
                    <Box sx={{ mt: 0.5, ml: 1.5 }}>
                        <em>Target or optimal values for this question (answer matches any)</em>
                    </Box>
                </FormControl>
            ) : (
                <TextField
                    fullWidth
                    label="Ideal Value"
                    value={formData.idealValue}
                    onChange={handleTextChange('idealValue')}
                    margin="normal"
                    helperText="Target or optimal value for this question"
                />
            )}

            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button onClick={onCancel}>
                    Cancel
                </Button>
                <Button 
                    type="submit" 
                    variant="contained" 
                    color="primary"
                    disabled={showOptionsInput && formData.answerOptions.length === 0}
                >
                    {initialData ? 'Update' : 'Create'}
                </Button>
            </Box>
        </Box>
    );
};

export default QuestionForm;